/*
 * Sizing a picture to the page, and snapping an edge while resizing (pure maths, tested in tests/fit.test.ts).
 *   fitInto   "เต็มหน้า" (cover: fills the box, the overflow is cut off when exported) / "พอดีหน้า" (contain: all of the
 *             picture inside, may leave space) — keeping its proportions, centred in the box
 *   snapEdge  while a handle is dragged: the moving edge(s) that are close to a guide line, and the scale that puts
 *             the edge exactly on it (the fixed side stays where it is)
 */

export interface Box { x: number; y: number; w: number; h: number }

/** scale and top-left position for a w×h picture (unscaled) in `box` */
export function fitInto(w: number, h: number, box: Box, mode: 'cover' | 'contain'): { scale: number; left: number; top: number } {
  if (!(w > 0 && h > 0)) throw new Error('รูปนี้ไม่มีขนาด จึงปรับให้พอดีหน้าไม่ได้');
  const scale = mode === 'cover' ? Math.max(box.w / w, box.h / h) : Math.min(box.w / w, box.h / h);
  return { scale, left: box.x + (box.w - w * scale) / 2, top: box.y + (box.h - h * scale) / 2 };
}

export interface SnapResult {
  /** multiply the scale by these (1 = unchanged) */
  fx: number; fy: number;
  /** guide lines to draw (design px) */
  guides: Array<{ x?: number; y?: number }>;
}

/**
 * `r` = the object's current box. `mx`/`my` = which edge the finger moves on each side ('l'/'r', 't'/'b', null = that
 * side does not move — from Fabric's transform origin, so it stays right after the handle is dragged past the other
 * side). `inset` = the part of the box that does not grow with the scale (an outline kept the same width), per side.
 * `uniform`: both sides keep one factor; a guide is taken only if the other moving edge stays within `th` of where
 * the finger put it (a thin object must not jump along its long side).
 */
export function snapEdge(r: Box, mx: 'l' | 'r' | null, my: 't' | 'b' | null, xs: number[], ys: number[], th: number,
  uniform: boolean, inset = { x: 0, y: 0 }): SnapResult {
  const out: SnapResult = { fx: 1, fy: 1, guides: [] };
  const near = (v: number, cands: number[]) => {
    let best: { d: number; c: number } | null = null;
    for (const c of cands) { const d = c - v; if (Math.abs(d) < th && (!best || Math.abs(d) < Math.abs(best.d))) best = { d, c }; }
    return best;
  };
  // one side: its moving edge, its fixed edge, and the part that scales; null when it does not move or has no size
  const side = (m: string | null, lo: number, size: number, ins: number) => {
    if (!m || size - ins < 1) return null;
    const moving = m === 'l' || m === 't' ? lo : lo + size, fixed = m === 'l' || m === 't' ? lo + size : lo;
    return { moving, fixed, grow: size - ins, ins };
  };
  const sx = side(mx, r.x, r.w, inset.x), sy = side(my, r.y, r.h, inset.y);
  const factor = (s: NonNullable<typeof sx>, c: number) => {
    const want = Math.abs(c - s.fixed) - s.ins;
    return want > 1 ? want / s.grow : 1;
  };
  const hx = sx && near(sx.moving, xs), hy = sy && near(sy.moving, ys);
  if (uniform) {
    // one factor for both sides: try the closer guide first; the other moving edge may shift at most th
    const tries: Array<['x' | 'y', number]> = [];
    if (hx) tries.push(['x', Math.abs(hx.d)]);
    if (hy) tries.push(['y', Math.abs(hy.d)]);
    tries.sort((a, b) => a[1] - b[1]);
    for (const [axis] of tries) {
      const k = axis === 'x' ? factor(sx!, hx!.c) : factor(sy!, hy!.c);
      const other = axis === 'x' ? sy : sx;
      if (other && Math.abs(k - 1) * other.grow > th) continue;
      out.fx = out.fy = k;
      out.guides.push(axis === 'x' ? { x: hx!.c } : { y: hy!.c });
      return out;
    }
    return out;
  }
  if (sx && hx) { out.fx = factor(sx, hx.c); out.guides.push({ x: hx.c }); }
  if (sy && hy) { out.fy = factor(sy, hy.c); out.guides.push({ y: hy.c }); }
  return out;
}

/** the edge a handle moves, from where Fabric keeps the object fixed: fixed 'left' → the right edge moves */
export function movingEdges(originX: unknown, originY: unknown, corner: string): { mx: 'l' | 'r' | null; my: 't' | 'b' | null } {
  const xs = corner !== 'mt' && corner !== 'mb', ys = corner !== 'ml' && corner !== 'mr';
  return {
    mx: !xs ? null : originX === 'left' ? 'r' : originX === 'right' ? 'l' : null,
    my: !ys ? null : originY === 'top' ? 'b' : originY === 'bottom' ? 't' : null
  };
}
